import { useEffect, useState } from 'react';

import { downloadCSVApi as downloadCSVApiReview } from 'api/analytics/review';
import { useToast } from 'components/hooks/useToast';

import { CSV_DOWNLOAD_API_POLL_INTERVAL, CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL } from 'constants/ProjectConstants';

import { getStatusOfRequestIds as getStatusOfRequestIdsReview } from 'services/analytics/review';

const wait = (ms = 1000) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const useCFCSVDownload = ({
  localStorageKey,
  fileName = '',
  getStatusOfRequestIds = getStatusOfRequestIdsReview,
  downloadCSVApi = downloadCSVApiReview,
  downloadFileHandler,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isRequestedFileGeneration, setIsRequestedFileGeneration] = useState(false);
  const toast = useToast();

  const onDownloadToSystemComplete = () => {
    try {
      window.localStorage.removeItem(localStorageKey);
      setIsFetching(false);
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const showErrorInDownloadingToast = () => {
    toast({
      title: 'Failed to download CSV file',
      status: 'error',
      duration: 3000,
    });
  };

  const onError = (error) => {
    setIsFetching(false);
    console.error('Error in CSV download : ', error);
    showErrorInDownloadingToast();
  };

  const downloadToSystem = async (blobData) => {
    try {
      if (blobData) {
        const { fetchedBlob: blob, requestId, name } = blobData;
        // 2. Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        if (fileName) {
          link.setAttribute('download', fileName);
        } else {
          link.setAttribute('download', name);
        }
        // 3. Append to html page
        document.body.appendChild(link);
        // 4. Force download
        link.click();
        // 5. Clean up and remove the link
        link.parentNode.removeChild(link);
        onDownloadToSystemComplete();
      }
    } catch (e) {
      onError(e);
    }
  };

  const showDownloadDoneToast = () => {
    toast({
      title: 'Download complete!',
      status: 'success',
    });
  };

  const showDownloadStartInfoToast = () => {
    toast({
      title: 'Generating report',
      status: 'info',
      duration: 2000,
    });
  };

  const getDownloadRequestFromLS = () => {
    try {
      const downloadRequests = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];
      return downloadRequests;
    } catch (e) {
      console.error('Error in Local storage : ', e);
    }
  };

  const [downloadRequestInLS, setDownloadRequestsInLS] = useState(getDownloadRequestFromLS);

  const setGeneratedFilesToLS = (res) => {
    try {
      const prevGeneratedFiles = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      const generatedFiles = [...prevGeneratedFiles, { ...res, requestedTS: Date.now() }];

      const stringifiedGeneratedFiles = JSON.stringify(generatedFiles);

      window.localStorage.setItem(localStorageKey, stringifiedGeneratedFiles);
      setDownloadRequestsInLS(generatedFiles);
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const generateFile = async () => {
    showDownloadStartInfoToast();
    setIsRequestedFileGeneration(true);
    try {
      if (isFetching) return;
      setIsFetching(true);
      const response = await downloadFileHandler();
      setGeneratedFilesToLS(response.entity);
    } catch (e) {
      console.error('Error in generating file : ', e);
      setIsFetching(false);
    }
  };

  const onSuccess = async (blobData) => {
    setIsFetching(false);
    await downloadToSystem(blobData);
    showDownloadDoneToast();
  };

  const downloadCSVBlob = async (requestId, name) => {
    try {
      const response = await downloadCSVApi(requestId);
      const fetchedBlob = await response.blob();
      onSuccess({
        fetchedBlob,
        requestId,
        name,
      });
    } catch (err) {
      console.error('Error in downloading CSV ', err);
    }
  };

  const apiPoll = async (fetchFunction, functionCondition, pollingInterval) => {
    try {
      let result = await fetchFunction();
      while (functionCondition(result)) {
        await wait(pollingInterval);
        result = await fetchFunction();
      }
      if (Array.isArray(result)) {
        result.forEach((result) => {
          downloadCSVBlob(result.uuid, result.name);
        });
      }
    } catch (err) {
      console.error('Error in fetching status of CSV ', err);
    }
  };

  const validateStatusResponse = (res = []) =>
    res
      .map((report) => {
        switch (report.status) {
          case 'IN_PROGRESS':
            return true;
          case 'FINISHED':
            return false;
          case 'ERROR': {
            onError('Error in generating file');
            return false;
          }
          default:
            onError('Error in generating file');

            return false;
        }
      })
      .reduce((prev, curr) => prev + curr, false);

  const getStatusForRequestIds = async () => {
    try {
      const downloadRequests = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      if (downloadRequests.length) {
        const currentTS = Date.now();

        // files which were requested CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL back are deleted from LS
        const validDownloadRequests = downloadRequests.filter(
          (request) => currentTS - request.requestedTS <= CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL
        );

        window.localStorage.setItem(localStorageKey, JSON.stringify(validDownloadRequests ?? []));

        validDownloadRequests.forEach((request) => {
          setIsFetching(true);
        });

        const response = await getStatusOfRequestIds(validDownloadRequests);
        return response;
      }
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  useEffect(() => {
    if (!isRequestedFileGeneration) return;
    apiPoll(getStatusForRequestIds, validateStatusResponse, CSV_DOWNLOAD_API_POLL_INTERVAL);
  }, [downloadRequestInLS]);

  useEffect(
    () => () => {
      window.localStorage.removeItem(localStorageKey);
      setIsFetching(false);
    },
    [localStorageKey]
  );

  return {
    generateFile,
    isFetching,
  };
};
